<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Trip Reviews ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-row v-show="showSearch" align="center" style="max-width: 300px;">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search review"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>
      </template>
    </v-app-bar>

    <div class="d-flex mt-6">
      <v-select
        hide-details
        flat
        solo
        dense
        label="All Reviews"
        clearable
        item-text="label"
        item-value="value"
        v-model="filter.review_type"
        class="mr-4"
        :items="[
          { value: null, label: 'All reviews' },
          { value: 'only_shown_reviews', label: 'Shown reviews' },
          { value: 'only_hidden_reviews', label: 'Hidden reviews' }
        ]"
        style="max-width: 200px"
        @change="filterReviews"
      />

      <v-select
        hide-details
        flat
        solo
        dense
        label="All Reviews"
        clearable
        item-text="label"
        item-value="value"
        v-model="filter.comment_type"
        class="mr-4"
        :items="[
          { value: null, label: 'All comments' },
          { value: 'only_shown_comments', label: 'Shown comments' },
          { value: 'only_hidden_comments', label: 'Hidden comments' }
        ]"
        style="max-width: 200px"
        @change="filterReviews"
      />

      <v-select
        hide-details
        flat
        solo
        dense
        label="All Reviews"
        clearable
        item-text="label"
        item-value="value"
        v-model="filter.photo_type"
        class="mr-4"
        :items="[
          { value: null, label: 'All photos' },
          { value: 'only_shown_photos', label: 'No hidden photos' },
          { value: 'only_hidden_photos', label: 'With hidden photos' }
        ]"
        style="max-width: 200px"
        @change="filterReviews"
      />

      <v-select
        hide-details
        flat
        solo
        dense
        label="All Reviews"
        clearable
        item-text="label"
        item-value="value"
        v-model="filter.user_type"
        :items="[
          { value: null, label: 'All users' },
          { value: 'only_blocked_users', label: 'Blocked Users' },
          { value: 'only_active_users', label: 'Active Users' }
        ]"
        style="max-width: 200px"
        @change="filterReviews"
      />

      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            class="ml-4"
            flat
            solo
            single-line
            clearable
            dense
            readonly
            v-on="on"
            v-model="reviewDateFormatted"
            placeholder="Review date"
            :append-icon="icons.calendar"
            @click:clear="clearDateFilter"
            style="max-width: 250px"
          ></v-text-field>
        </template>

        <v-date-picker
          v-model="filter.date_reviewed"
          no-title
          scrollable
          color="primary"
          :max="currentDate"
          @input="filterReviews"
        ></v-date-picker>
      </v-menu>
    </div>

    <div
      class="mx-4 pb-10"
      id="review-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
      min-width="200px"
    >
      <div v-for="review in reviews" :key="review.user_trip_review_id">
        <review-details-card :review="review" />
      </div>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(reviews.length > 0) && !loading"
      >
        <p>No match found.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiPencil,
  mdiTrashCanOutline
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'
import ReviewDetailsCard from './components/ReviewDetailsCard'
import dayjs from 'dayjs'

export default {
  name: 'ReviewList',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll
  },

  components: {
    AppBarNavIcon,
    ReviewDetailsCard
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      showProfile: false,
      selectedProfile: null,
      menu: false,
      reviewDateFormatted: '',
      filter: {
        search: '',
        review_type: null,
        comment_type: null,
        photo_type: null,
        user_type: 'only_active_users',
        date_reviewed: ''
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        edit: mdiPencil,
        delete: mdiTrashCanOutline
      },
      showDeleteModal: false,
      deleteReviewId: null
    }
  },

  created() {
    this.clearReviews()
    this.fetchReviews(1)
  },

  computed: {
    ...mapState({
      reviews: state => state.review.list,
      listMeta: state => state.review.listMeta
    }),

    currentDate() {
      return dayjs(new Date()).format('YYYY-MM-DD')
    },

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    }
  },

  methods: {
    ...mapActions({
      getReviews: 'review/getReviews',
      setAlert: 'alert/setAlert'
    }),

    ...mapMutations({
      clearReviews: 'review/clearReviewList'
    }),

    pageChanged(page) {
      this.fetchReviews(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchReviews(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearReviews()
      this.fetchReviews()
    }, 600),

    async fetchReviews(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      if (this.filter.review_type) {
        params.review_type = this.filter.review_type
      }
      if (this.filter.comment_type) {
        params.comment_type = this.filter.comment_type
      }
      if (this.filter.photo_type) {
        params.photo_type = this.filter.photo_type
      }
      if (this.filter.user_type) {
        params.user_type = this.filter.user_type
      }
      if (this.filter.date_reviewed) {
        params.date_reviewed = this.filter.date_reviewed
      }

      this.loading = true
      await this.getReviews(params)
      this.loading = false
    },

    filterReviews(filterValue) {
      if (this.filter.date_reviewed) {
        this.reviewDateFormatted = `${dayjs(this.filter.date_reviewed).format(
          'MMMM DD, YYYY'
        )}`
      }
      this.clearReviews()
      this.fetchReviews(null, filterValue)
      this.menu = false
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },

    clearDateFilter() {
      this.filter.date_reviewed = ''
      this.filterReviews()
    }
  }
}
</script>
